import { useMutation } from "@apollo/client";
import ModeCommentOutlined from "@mui/icons-material/ModeCommentOutlined";
import { Paper, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import { gql } from "generated-graphql";
import { CommentThreadFragment } from "generated-graphql/graphql";
import { DELETE_COMMENT } from "hooks/comments";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useEffect, useState } from "react";

const UPSERT_WORK_COMMENT = gql(`
  mutation UpsertWorkComment($input: CommentInput!) {
    upsertComment(input: $input) {
      ...CommentThread
    }
  }
`);

type WorkCommentsPanelProps = {
  workflowId: string;
  comments: CommentThreadFragment[];
};

/**
 * The deliverable's unified comment stream: one thread on the workflow
 * itself, for conversation about the work as a whole rather than any one
 * step.
 */
export function WorkCommentsPanel({
  workflowId,
  comments: incomingComments,
}: WorkCommentsPanelProps) {
  const alerts = useAlerts();
  const { user } = useCurrentUser();
  const [comments, setComments] = useState<CommentThreadFragment[]>(incomingComments);

  useEffect(() => {
    setComments(incomingComments ?? []);
  }, [incomingComments]);

  const [upsertComment, { loading: addingComment }] = useMutation(UPSERT_WORK_COMMENT, {
    onError: (error) => {
      alerts.error("Failed to add comment", error);
    },
  });
  const [deleteComment, { loading: deletingComment }] = useMutation(DELETE_COMMENT, {
    onError: (error) => {
      alerts.error("Failed to delete comment", error);
    },
  });

  const handleAddComment = async (message: string) => {
    const result = await upsertComment({
      variables: { input: { workflowId, comment: message } },
    });
    const newComment = result.data?.upsertComment;
    if (newComment) {
      setComments((existing) => [...existing, newComment]);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    await deleteComment({ variables: { id: commentId } });
    setComments((existing) => existing.filter((comment) => comment.id !== commentId));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ModeCommentOutlined fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="h6">Comments</Typography>
      </Stack>
      <GenericCommentStream
        title=""
        comments={comments}
        currentUserId={user?.id}
        onAddComment={handleAddComment}
        onDeleteComment={handleDeleteComment}
        addLoading={addingComment}
        deleteLoading={deletingComment}
        placeholder="Comment on this deliverable…"
        buttonText="Comment"
        submitButtonVariant="icon"
        inputRows={2}
        inputMaxRows={8}
        emptyStateMessage="No comments yet — this thread covers the whole deliverable."
      />
    </Paper>
  );
}
